/* eslint-disable */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import PrimaryActionButton from '../sidepanel/PrimaryActionButton';
import { useVideoCreatorStore } from '../../stores-v2/VideoCreatorStoreContext';

import CloudIcon from '../../svgs/CloudIcon';
import LongArrowUpIcon from '../../svgs/LongArrowUpIcon';
import LongArrowDownIcon from '../../svgs/LongArrowDownIcon';
import DeleteIcon from '../../svgs/DeleteIcon';

interface FileWithIndex extends File {
  index: number;
}

interface MultiFileUploadConcatProps {
  handleStoryVideoUpload?: (files: File[]) => void;
  onFilesChange?: (files: File[]) => void;
  onClose?: () => void;
  maxUploadSizeGB?: number;
}

export default observer((props: MultiFileUploadConcatProps) => {
  const {
    handleStoryVideoUpload,
    onFilesChange,
    onClose,
    maxUploadSizeGB = 2,
  } = props;
  const [files, setFiles] = useState<FileWithIndex[]>([]);
  const [recentlyMovedItems, setRecentlyMovedItems] = useState<number[]>([]);
  const videoCreator = useVideoCreatorStore();

  const maxFileSize = maxUploadSizeGB * 1024 * 1024 * 1024; // Convert GB to bytes
  const acceptedFormats = useMemo(
    () => ['video/mp4', 'video/quicktime', 'video/mov'],
    [],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);

      const validFiles = selectedFiles.filter(file => {
        if (file.size > maxFileSize) {
          videoCreator.toastState = {
            state: 'error',
            message: `${file.name} exceeds ${maxUploadSizeGB}GB limit`,
          };
          return false;
        }

        if (!acceptedFormats.some(format => file.type.includes(format))) {
          videoCreator.toastState = {
            state: 'error',
            message: `${file.name} is not a supported video format`,
          };
          return false;
        }

        return true;
      });

      const filesWithIndex = validFiles.map((file, index) =>
        Object.assign(file, { index: files.length + index }),
      );

      setFiles(prev => [...prev, ...filesWithIndex]);
    },
    [files.length, videoCreator, acceptedFormats, maxFileSize],
  );

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter(file => file.index !== index));
  };

  const moveFile = (currentIndex: number, direction: 'up' | 'down') => {
    setFiles(prev => {
      const fileIndex = prev.findIndex(f => f.index === currentIndex);
      if (fileIndex === -1) return prev;

      const newIndex = direction === 'up' ? fileIndex - 1 : fileIndex + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;

      const updated = [...prev];
      const temp = updated[fileIndex];
      updated[fileIndex] = updated[newIndex];
      updated[newIndex] = temp;

      // Add the moved item to recently moved
      setRecentlyMovedItems(prevMoved =>
        prevMoved.includes(currentIndex)
          ? prevMoved
          : [...prevMoved, currentIndex],
      );
      // Remove from recently moved after 1 second
      setTimeout(() => {
        setRecentlyMovedItems(prevMoved =>
          prevMoved.filter(id => id !== currentIndex),
        );
      }, 1000);

      return updated;
    });
  };

  // Notify parent component when files change
  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(files);
    }
    // Also call handleStoryVideoUpload for backward compatibility
    if (handleStoryVideoUpload && files.length > 0) {
      handleStoryVideoUpload(files);
    }
  }, [files, onFilesChange, handleStoryVideoUpload]);

  const handleSubmit = () => {
    if (files.length === 0) {
      videoCreator.toastState = {
        state: 'error',
        message: 'Please select files to upload',
      };
      return;
    }

    // Pass the files to the parent component
    if (handleStoryVideoUpload) {
      handleStoryVideoUpload(files);
    }
  };

  return (
    <Container>
      {onClose && (
        <Header>
          <div />
          <CloseButton onClick={onClose} aria-label="Remove file">
            <svg
              width={12}
              height={12}
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M11 1 1 11M1 1l10 10"
                stroke="#F3E9D7"
                strokeWidth="1.667"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </CloseButton>
        </Header>
      )}

      <UploadArea>
        <FileInput
          type="file"
          multiple
          accept={acceptedFormats.join(',')}
          onChange={handleFileSelect}
          id="multi-file-input"
        />
        <UploadLabel htmlFor="multi-file-input">
          <CloudIcon width="20" height="20" />
          <UploadText>Click to select videos or drag and drop</UploadText>
          <UploadSubtext>MP4, MOV, QuickTime (max 2GB total)</UploadSubtext>
        </UploadLabel>
      </UploadArea>

      {files.length > 0 && (
        <>
          <FileListHeader>Files to combine ({files.length})</FileListHeader>
          <FileList>
            {files.map((file, index) => {
              const isRecentlyMoved = recentlyMovedItems.includes(file.index);
              return (
                <FileItem key={file.index} isRecentlyMoved={isRecentlyMoved}>
                  <FileInfo>
                    <FileNumber isRecentlyMoved={isRecentlyMoved}>
                      {index + 1}
                    </FileNumber>
                    <FileDetails>
                      <FileName>{file.name}</FileName>
                      <FileSize>
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </FileSize>
                    </FileDetails>
                  </FileInfo>

                  <FileActions>
                    <MoveButtons>
                      {index > 0 && (
                        <IconButton onClick={() => moveFile(file.index, 'up')}>
                          <LongArrowUpIcon
                            width="8"
                            height="10"
                            fillColor="#F3E9D7"
                          />
                        </IconButton>
                      )}
                      {index < files.length - 1 && (
                        <IconButton
                          onClick={() => moveFile(file.index, 'down')}
                        >
                          <LongArrowDownIcon
                            width="8"
                            height="10"
                            fillColor="#F3E9D7"
                          />
                        </IconButton>
                      )}
                    </MoveButtons>
                    <RemoveFileIconButton
                      onClick={() => removeFile(file.index)}
                    >
                      <DeleteIcon
                        strokeColor="#848484"
                        width="20"
                        height="20"
                      />
                    </RemoveFileIconButton>
                  </FileActions>
                </FileItem>
              );
            })}
          </FileList>
        </>
      )}
    </Container>
  );
});

const Container = styled.div`
  padding: 0;
  background: transparent;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #6b7280;

  &:hover {
    color: #374151;
  }
`;

const UploadArea = styled.div`
  position: relative;
  width: 440px;
  margin-left: auto;
  margin-right: auto;
  &:hover {
    label {
      background-color: #484848;
      border-color: #f2d093;
    }
  }
`;

const FileInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const UploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 118px;
  background-color: #262630;
  border: 1px dashed #848484;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
`;

const UploadText = styled.p`
  margin: 8px 0 4px;
  font-weight: 400;
  color: #f3e9d7;
  font-size: 14px;
  font-weight: 700;
  line-height: 140%;
  text-align: center;
`;

const UploadSubtext = styled.p`
  margin: 0;
  font-weight: 400;
  color: #ef5d6f;
  font-size: 12px;
  line-height: 140%;
  text-align: center;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(72, 72, 72, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #484848;
    border-radius: 4px;

    &:hover {
      background: #525252;
    }
  }
`;

const FileListHeader = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: #f3e9d7;
  margin: 0 0 16px;
  padding: 0;
`;

const FileItem = styled.div<{ isRecentlyMoved?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  background: #262630;
  border-radius: 8px;
  border: 1px solid ${props => (props.isRecentlyMoved ? '#17C964' : '#484848')};
  transition: border-color 0.3s ease;
  &:hover {
    border-color: ${props => (props.isRecentlyMoved ? '#17C964' : '#f2d093')};
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const FileNumber = styled.span<{ isRecentlyMoved?: boolean }>`
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid;
  border-radius: 24px;
  transition: all 0.2s;
  background: ${props => (props.isRecentlyMoved ? '#17C964' : 'transparent')};
  border-color: ${props => (props.isRecentlyMoved ? '#17C964' : '#f3e9d7')};
  color: ${props => (props.isRecentlyMoved ? '#03041A' : '#f3e9d7')};
`;

const FileDetails = styled.div``;

const FileName = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #f3e9d7;
  line-height: 140%;
  max-width: 232px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
`;

const FileSize = styled.p`
  margin: 0;
  font-size: 12px;
  color: #848484;
  line-height: 140%;
`;

const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MoveButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #484848;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.2s;
  color: #f3e9d7;
  svg path {
    transition: all 0.2s;
  }

  &:hover {
    background: #f2d093;
    svg path {
      stroke: #03041a;
    }
  }
`;

const RemoveFileIconButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  svg path {
    transition: stroke 0.2s;
  }
  &:hover svg path {
    stroke: #ef5d6f;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;
